import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaEnvelope, FaBuilding } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import styles from './SignupForm.module.css';

const SignupForm: React.FC = () => {
  const { signup, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organisation: ''
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [successMessage, setSuccessMessage] = useState<string>('');

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.organisation) {
      newErrors.organisation = 'Organisation name is required';
    } else if (formData.organisation.length < 2) {
      newErrors.organisation = 'Organisation name must be at least 2 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setSuccessMessage('');
    
    if (!validateForm()) {
      return;
    }

    try {
      await signup({
        name: formData.name,
        email: formData.email,
        organisation: formData.organisation
      });
      
      // Show success message and redirect to login
      setSuccessMessage('Details submitted successfully! The Team will get back to you soon.');
      setTimeout(() => {
        // navigate('/login');
        console.log('Details submitted successfully! The Team will get back to you soon.');
      }, 2000);
      
    } catch (error) {
      console.error('Signup failed:', error);
      // Error is already set in the context
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Clear success message when user starts typing
    if (successMessage) {
      setSuccessMessage('');
    }
  };

  return (
    <div className={styles.signupForm}>
      <div className={styles.header}>
        <div className={styles.logoContainer}>
          <FaUser className={styles.lockIcon} />
          <h1 className={styles.appName}>Policy.net</h1>
        </div>
        <p className={styles.subtitle}>Request Access to Policy.net</p>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className={styles.successMessage}>
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className={styles.errorMessage}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.inputGroup}>
          <div className={styles.inputWrapper}>
            <FaUser className={styles.inputIcon} />
            <input
              type="text"
              name="name"
              placeholder="Full Name"
              value={formData.name}
              onChange={handleInputChange}
              className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
            />
          </div>
          {errors.name && <span className={styles.errorText}>{errors.name}</span>}
        </div>

        <div className={styles.inputGroup}>
          <div className={styles.inputWrapper}>
            <FaEnvelope className={styles.inputIcon} />
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleInputChange}
              className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
            />
          </div>
          {errors.email && <span className={styles.errorText}>{errors.email}</span>}
        </div>

        <div className={styles.inputGroup}>
          <div className={styles.inputWrapper}>
            <FaBuilding className={styles.inputIcon} />
            <input
              type="text"
              name="organisation"
              placeholder="Organisation Name"
              value={formData.organisation}
              onChange={handleInputChange}
              className={`${styles.input} ${errors.organisation ? styles.inputError : ''}`}
            />
          </div>
          {errors.organisation && <span className={styles.errorText}>{errors.organisation}</span>}
        </div>

        <button
          type="submit"
          className={styles.signupButton}
          disabled={isLoading}
        >
          {isLoading ? 'Sending your info...' : 'Request Access'}
        </button>
      </form>

      <div className={styles.loginLink}>
        <p>
          Already have an account?{' '}
          <button 
            type="button" 
            className={styles.link} 
            onClick={() => navigate('/login')}
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
};

export default SignupForm;
